import { faBuilding } from '@fortawesome/pro-solid-svg-icons/faBuilding';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faHouse } from '@fortawesome/pro-solid-svg-icons/faHouse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Group, Stack, Text } from '@mantine/core';
import { useIntl } from 'react-intl';

import { addressValidationMessages } from './address-validation-messages';
import styles from './address-validation.module.css';
import { RESIDENTIAL_INDICATOR } from '../../../../../../../address/address-validation.api';

import type { AddressValidationResultItem, MatchedAddress } from '../../../../../../../address/address-validation.api';

type SuggestionCardProps = {
  result: AddressValidationResultItem;
  isValidating: boolean;
  onValidate: () => void;
  onApply: (matched: MatchedAddress) => void;
};

// TODO: use country-specific formatting
const formatMatchedAddress = (matched: MatchedAddress): string => {
  const streetLines = [matched.line1, matched.line2, matched.line3].filter(Boolean);
  const stateZip = [matched.state, matched.postalCode].filter(Boolean).join(' ');
  const cityLine = [matched.city, stateZip, matched.countryCode].filter(Boolean).join(', ');
  return [...streetLines, cityLine].filter(Boolean).join(', ');
};

export const SuggestionCard = ({ result, isValidating, onValidate, onApply }: SuggestionCardProps) => {
  const intl = useIntl();
  const { matchedAddress } = result;

  if (!matchedAddress) {
    return (
      <Stack className={styles.suggestionCard} gap='xs'>
        <Group gap='xs'>
          <FontAwesomeIcon icon={faCircleCheck} />
          <Text className={styles.suggestionTitle} size='sm'>
            {intl.formatMessage(addressValidationMessages.addressVerified)}
          </Text>
        </Group>
        <Button disabled={isValidating} onClick={onValidate} size='condensed' variant='ghost'>
          {intl.formatMessage(addressValidationMessages.revalidateButton)}
        </Button>
      </Stack>
    );
  }

  const isResidential = matchedAddress.residentialIndicator === RESIDENTIAL_INDICATOR.RESIDENTIAL;
  const icon = isResidential ? faHouse : faBuilding;
  const titleMessage = isResidential
    ? intl.formatMessage(addressValidationMessages.suggestedResidential)
    : intl.formatMessage(addressValidationMessages.suggestedCommercial);

  const handleApply = () => {
    onApply(matchedAddress);
  };

  return (
    <Stack className={styles.suggestionCard} gap='xs'>
      <Group gap='xs'>
        <FontAwesomeIcon icon={icon} />
        <Text className={styles.suggestionTitle} size='sm'>
          {titleMessage}
        </Text>
      </Group>
      <Text className={styles.suggestionAddress}>{formatMatchedAddress(matchedAddress)}</Text>
      <Group gap='md'>
        <Button onClick={handleApply} size='condensed' variant='tertiary'>
          {intl.formatMessage(addressValidationMessages.useThisAddress)}
        </Button>
        <Button disabled={isValidating} onClick={onValidate} size='condensed' variant='ghost'>
          {intl.formatMessage(addressValidationMessages.revalidateButton)}
        </Button>
      </Group>
    </Stack>
  );
};
